/**
 * Karte „Heat-Up-Wellen je Webinar" (P4, Plan 2026-10-27 Paket A Nr. 1). Oben der Vergleich der drei Wellen über die
 * gelaufenen Webinare mit allen drei Wellen (gemeinsame Basis), darunter je Webinar die Wellen mit Versandzeit — eine
 * fehlende Welle steht als „fehlt", nie als 0. Backend: /api/auswertungen/hebel/wellen.
 * Persona-Umbau A2 (Plan 2026-10-28): im Vergleich ist der Erwartete Wert je 1.000 Kontakte der Hauptwert, bewertet wie P3
 * „Webinar-Einladungen" (Backend `webinarBasis.wert_je_anfo`), dazu Abmeldungen je Anmeldung. Je Webinar sind die Quoten je
 * 1.000 Kontakte der Hauptwert, die Anzahlen klein; je Welle und Webinar kein Euro (zu wenige Anfos).
 */
import { FC, ReactNode, useMemo } from 'react';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, Hinweis, Laden, int } from '../../../components/auswertungen/kennzahlTeile';
import { ATTRIBUTION, GLOSSAR, TERMS } from '../../../helpers/kpiDefinitions';
import type { Versandzeit, WelleKey, WellenAntwort, WellenQuoten, WellenVergleich, WellenWebinar, WellenZahlen } from '../../../helpers/hebelData';
import { formatNumber } from '../../../helpers/numberUtils';
import { euroJe1000, festeStellen, wellenBefund } from './befund';
import { tag, Unterzeile, versandZeit } from './teile';

/**
 * Vergleich der Wellen (P5-Review #30): die Quote je 1.000 ist der Hauptwert (die Anzahlen haben verschiedene Nenner),
 * mit festen Nachkommastellen je Spalte; die Anzahl steht klein darunter.
 */
const QuoteMitZahl: FC<{ je: number | null; n: number | null; stellen: number; was: string; grau?: string | null }> = ({ je, n, stellen, was, grau }) => {
  if (je === null || n === null) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        —
      </Box>
    );
  }
  const inhalt: ReactNode = (
    <Box sx={{ lineHeight: 1.25, color: grau ? 'text.disabled' : undefined, cursor: grau ? 'help' : undefined }}>
      {formatNumber(je, stellen)}
      <Unterzeile>
        {int(n)} {was}
      </Unterzeile>
    </Box>
  );
  return grau ? (
    <Tooltip title={grau} arrow placement="top">
      {inhalt as React.ReactElement}
    </Tooltip>
  ) : (
    <>{inhalt}</>
  );
};

const TEILNAHME_GRUND: Record<'messluecke' | 'tracking_warnung', string> = {
  messluecke: 'Bekannte Messlücke der Teilnahme (oder Anmeldungen übernommen) — Teilnehmer nicht verlässlich.',
  tracking_warnung: 'Tracking-Warnung: der Anteil Teilnehmer unter den vor Beginn Angemeldeten liegt außerhalb des plausiblen Bereichs.',
};

interface Zeile {
  id: string;
  art: 'webinar' | 'welle' | 'redlink' | 'nicht_oeffner' | 'nach_webinartag';
  titel: string;
  unter?: string;
  start: string;
  zahlen: WellenZahlen | null;
  quoten: WellenQuoten | null;
  fehlt?: boolean;
  zeiten?: Versandzeit[];
  webinar: WellenWebinar;
  kinder?: Zeile[];
}

const WELLEN_RANG: Record<WelleKey, number> = { vortag: 1, frueh: 2, spaet: 3 };

const ohneQuote: WellenQuoten = {
  anmeldungen_je_1000: null,
  teilnehmer_je_1000: null,
  anfos_je_1000: null,
  abmeldungen_je_1000: null,
  wert_je_1000: null,
  abmeldungen_je_anmeldung: null,
};

/**
 * Zelle der Tabelle je Webinar (A2: Quote groß, Anzahl klein). Ohne Quote (Redlink, Nachversand — keine Welle) nur die
 * Anzahl; Teilnehmer `null` (Messlücke) als „—" mit Grund.
 */
const QuoteZelle: FC<{ je: number | null | undefined; n: number | null; stellen: number; was: string; fehltGrund?: string }> = ({ je, n, stellen, was, fehltGrund }) => {
  if (n === null) {
    return (
      <Tooltip title={fehltGrund ?? 'nicht messbar'} arrow placement="top">
        <Box component="span" sx={{ cursor: 'help', color: 'text.secondary' }}>
          —
        </Box>
      </Tooltip>
    );
  }
  if (je === null || je === undefined) return <>{int(n)}</>;
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {formatNumber(je, stellen)}
      <Unterzeile>
        {int(n)} {was}
      </Unterzeile>
    </Box>
  );
};

function zeilenAus(w: WellenWebinar): Zeile {
  const kinder: Zeile[] = w.wellen.map((x) => ({
    id: `${w.webinar_id}/${x.welle}`,
    art: 'welle',
    titel: x.label,
    start: w.start_date,
    zahlen: x.fehlt ? null : x,
    quoten: x.fehlt ? null : x,
    fehlt: x.fehlt,
    zeiten: x.zeiten,
    webinar: w,
  }));
  const extra = (art: Zeile['art'], titel: string, unter: string, z: WellenZahlen | null) =>
    z ? [{ id: `${w.webinar_id}/${art}`, art, titel, unter, start: w.start_date, zahlen: z, quoten: ohneQuote, webinar: w } as Zeile] : [];
  return {
    id: w.webinar_id,
    art: 'webinar',
    titel: w.public_title ?? w.webinar_id,
    start: w.start_date,
    // Ganz ohne Welle: „—" statt 0 (keine Welle fand statt).
    zahlen: w.fehlende_wellen === w.wellen.length ? null : w.summe,
    quoten: w.fehlende_wellen === w.wellen.length ? null : w.summe,
    webinar: w,
    kinder: [
      ...kinder,
      ...extra('redlink', 'Redlink-Mailing', 'keine Welle; zählt in „Webinare & Produkte“ mit', w.ausserhalb.redlink),
      ...extra('nicht_oeffner', 'Nachversand an Nicht-Öffner', 'keine Welle; zählt dort nicht', w.ausserhalb.nicht_oeffner),
      ...extra('nach_webinartag', 'Heat-Up nach dem Webinartag', 'keine Welle', w.ausserhalb.nach_webinartag),
    ],
  };
}

/** Versandzeiten einer Welle: „Sa 25.10. 06:00 · 09:00 nur dnv". */
const Zeiten: FC<{ zeiten: Versandzeit[] }> = ({ zeiten }) => (
  <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal' }}>
    {zeiten.map((z, i) => (
      <Box component="span" key={z.zeit} sx={{ display: 'block', fontSize: 12 }}>
        {i === 0 || z.zeit.slice(0, 10) !== zeiten[i - 1].zeit.slice(0, 10) ? versandZeit(z.zeit) : z.zeit.slice(11, 16)}
        {z.teilmenge && (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {' '}
            {z.ohne.length < z.clients.length ? `ohne ${z.ohne.join(', ')}` : `nur ${z.clients.join(', ')}`}
          </Box>
        )}
        {z.ganze_liste && (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {' '}
            · ganze Liste
          </Box>
        )}
        {z.nach_beginn && (
          <Box component="span" sx={{ color: 'warning.main' }}>
            {' '}
            · nach Beginn
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

/**
 * D16: Standardspalten der Tabelle „je Webinar" je Kartenbreite (am Handy Name, Anmeldungen, Anfos — wie bisher fest
 * verdrahtet; unter 1.000 px Kartenbreite ohne „Versand", der steht dann unter dem Namen der Welle). Wählbar im Menü.
 */
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['name', 'versand', 'kontakte', 'anmeldungen', 'teilnehmer', 'anfos', 'abmeldungen'],
  mittel: ['name', 'kontakte', 'anmeldungen', 'teilnehmer', 'anfos', 'abmeldungen'],
  schmal: ['name', 'anmeldungen', 'anfos'],
};

const WellenKarte: FC<{ data: WellenAntwort | null; fehler: string | null }> = ({ data, fehler }) => {
  const g = data?.grau_schwellen ?? { anfos: 20, anmeldungen: 50, rechtzeitig: 50, abmeldungen: 20 };
  const teilnahmeGrund = (w: WellenWebinar) => (w.teilnahme_grund ? TEILNAHME_GRUND[w.teilnahme_grund] : undefined);
  const theme = useTheme();
  // Telefon: nur Name und Hauptwerte, sonst lagen „€ je 1.000" und „Anmeldungen je 1.000" außerhalb des Bildes
  // (Darstellungsprüfung Runde 2). Die übrigen Spalten gibt es ab Tablet-Breite.
  const telefon = useMediaQuery(theme.breakpoints.down('sm'));
  const vz = data?.vergleich.zeilen ?? [];
  const st = {
    anmeldungen: festeStellen(vz.map((r) => r.anmeldungen_je_1000)),
    teilnehmer: festeStellen(vz.map((r) => r.teilnehmer_je_1000)),
    anfos: festeStellen(vz.map((r) => r.anfos_je_1000)),
    abmeldungen: festeStellen(vz.map((r) => r.abmeldungen_je_1000)),
    abmJeAnm: festeStellen(vz.map((r) => r.abmeldungen_je_anmeldung)),
  };
  const befund = useMemo(() => (data ? wellenBefund(data.vergleich.zeilen, data.vergleich.webinare, g.anfos) : null), [data, g.anfos]);
  const webinarZeilen = useMemo(() => (data ? data.webinare.map(zeilenAus) : []), [data]);
  // Feste Stellen je Spalte über alle Webinar- und Wellenzeilen (#30).
  const stJe = useMemo(() => {
    const alle = webinarZeilen.flatMap((z) => [z, ...(z.kinder ?? [])]).filter((z) => z.art === 'webinar' || z.art === 'welle');
    return {
      anmeldungen: festeStellen(alle.map((z) => z.quoten?.anmeldungen_je_1000)),
      teilnehmer: festeStellen(alle.map((z) => (z.zahlen?.teilnehmer === null ? null : z.quoten?.teilnehmer_je_1000))),
      abmeldungen: festeStellen(alle.map((z) => z.quoten?.abmeldungen_je_1000)),
    };
  }, [webinarZeilen]);
  const ueber = `${TERMS.wellenJe1000.label} · ${TERMS.wellenUeberHeatUp.short}`;

  // ── Vergleich der Wellen (gemeinsame Webinare) ──
  const vergleichSpalten: Spalte<WellenVergleich>[] = [
    // Sortierbar in zeitlicher Reihenfolge, damit man nach einem Klick auf eine Zahlenspalte zurück zu Vortag/früh/spät kommt.
    { key: 'welle', label: TERMS.welle.label, tip: TERMS.welle.tip, sortValue: (r) => WELLEN_RANG[r.welle], render: (r) => <Box sx={{ fontWeight: 600 }}>{r.label}</Box> },
    {
      key: 'kontakte',
      label: TERMS.wellenKontakte.label,
      tip: TERMS.wellenKontakte.tip,
      align: 'right',
      render: (r) => int(r.kontakte),
      sortValue: (r) => r.kontakte,
    },
    {
      key: 'anmeldungen',
      label: TERMS.wellenAnmeldungen.short,
      tip: TERMS.wellenAnmeldungen.tip,
      einheit: `${TERMS.wellenJe1000.label}, darunter Anzahl`,
      align: 'right',
      render: (r) => (
        <QuoteMitZahl je={r.anmeldungen_je_1000} n={r.anmeldungen} stellen={st.anmeldungen} was="Anm." grau={r.anmeldungen < g.anmeldungen ? `unter ${g.anmeldungen} Anmeldungen` : null} />
      ),
      sortValue: (r) => r.anmeldungen_je_1000,
    },
    {
      key: 'teilnehmer',
      label: TERMS.wellenTeilnehmer.short,
      tip: TERMS.wellenTeilnehmer.tip,
      hinweis: 'Je 1.000 gerechnet über die Kontakte der Webinare mit verlässlicher Teilnahme.',
      einheit: `${TERMS.wellenJe1000.label}, darunter Anzahl`,
      align: 'right',
      render: (r) => <QuoteMitZahl je={r.teilnehmer_je_1000} n={r.teilnehmer} stellen={st.teilnehmer} was="Teiln." />,
      sortValue: (r) => r.teilnehmer_je_1000,
    },
    {
      key: 'wert',
      label: TERMS.wellenWertJe1000.short,
      tip: `${TERMS.wellenWertJe1000.tip} Darunter: ${TERMS.wellenAnfos.tip}`,
      einheit: 'je 1.000 Kontakte, darunter Anfos',
      align: 'right',
      render: (r) => {
        const grau = r.anfos - r.anfos_vorlaeufig < g.anfos ? `unter ${g.anfos} Anfos — der Wert kann am Zufall hängen` : null;
        const inhalt = (
          <Box sx={{ lineHeight: 1.25, color: grau ? 'text.disabled' : undefined, cursor: grau ? 'help' : undefined }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {euroJe1000(r.wert_je_1000)}
            </Box>
            <Unterzeile>
              {r.anfos_je_1000 === null ? '—' : formatNumber(r.anfos_je_1000, st.anfos)} Anfos je 1.000 · {int(r.anfos)} Anfos
            </Unterzeile>
          </Box>
        );
        return grau ? (
          <Tooltip title={grau} arrow placement="top">
            {inhalt}
          </Tooltip>
        ) : (
          inhalt
        );
      },
      sortValue: (r) => r.wert_je_1000,
    },
    {
      key: 'abmeldungen',
      label: TERMS.wellenAbmeldungen.short,
      tip: TERMS.wellenAbmeldungen.tip,
      einheit: `${TERMS.wellenJe1000.label}, darunter Anzahl`,
      align: 'right',
      render: (r) => (
        <QuoteMitZahl je={r.abmeldungen_je_1000} n={r.abmeldungen} stellen={st.abmeldungen} was="Abm." grau={r.abmeldungen < g.abmeldungen ? `unter ${g.abmeldungen} Abmeldungen` : null} />
      ),
      sortValue: (r) => r.abmeldungen_je_1000,
    },
    {
      key: 'abm_je_anm',
      label: TERMS.wellenAbmJeAnm.short,
      tip: TERMS.wellenAbmJeAnm.tip,
      einheit: 'Abwägung Ertrag / Liste',
      align: 'right',
      render: (r) => {
        const grau = r.abmeldungen < g.abmeldungen || r.anmeldungen < g.anmeldungen;
        return (
          <Box component="span" sx={{ color: grau ? 'text.disabled' : undefined }} title={grau ? `unter ${g.abmeldungen} Abmeldungen oder ${g.anmeldungen} Anmeldungen` : undefined}>
            {r.abmeldungen_je_anmeldung === null ? '—' : formatNumber(r.abmeldungen_je_anmeldung, Math.max(1, st.abmJeAnm))}
          </Box>
        );
      },
      sortValue: (r) => r.abmeldungen_je_anmeldung,
    },
  ];

  // ── Je Webinar ──
  const quote = (z: Zeile, k: keyof WellenQuoten) => (z.art === 'webinar' || z.art === 'welle' ? (z.quoten?.[k] ?? null) : undefined);
  // P5-Review #28: bei 390 px füllten feste erste Spalte + „Versand" die ganze Breite — ohne die Spalte steht der Versand
  // unter dem Namen der Welle.
  const baue = (mitVersand: boolean): Spalte<Zeile>[] => [
    {
      key: 'name',
      pflicht: true,
      label: TERMS.wellenWebinarWelle.label,
      tip: `${TERMS.wellenWebinarWelle.tip} ${TERMS.welle.tip} ${TERMS.welleFehlt.tip}`,
      minWidth: 150,
      sortValue: (r) => r.start,
      render: (r) =>
        r.art === 'webinar' ? (
          <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal', maxWidth: 320 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {r.titel}
            </Box>
            <Unterzeile>
              {tag(r.start.slice(0, 10))} · {r.webinar.webinar_id}
              {r.webinar.kommend && ' · läuft noch'}
              {r.webinar.fehlende_wellen > 0 && (
                <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  {' '}
                  · {data!.soll - r.webinar.fehlende_wellen} von {data!.soll} Wellen
                </Box>
              )}
            </Unterzeile>
          </Box>
        ) : (
          <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal' }}>
            <Box component="span" sx={{ color: r.fehlt ? 'warning.main' : r.art === 'welle' ? 'text.primary' : 'text.secondary', fontWeight: r.fehlt ? 600 : 400 }}>
              {r.titel}
              {r.fehlt && ` — ${TERMS.welleFehlt.short}`}
            </Box>
            {r.unter && <Unterzeile>{r.unter}</Unterzeile>}
            {!mitVersand && r.zeiten && r.zeiten.length > 0 && (
              <Box sx={{ color: 'text.secondary', mt: 0.25 }}>
                <Zeiten zeiten={r.zeiten} />
              </Box>
            )}
          </Box>
        ),
    },
    {
      key: 'versand',
      label: TERMS.wellenVersand.label,
      tip: TERMS.wellenVersand.tip,
      minWidth: 120,
      render: (r: Zeile) => (r.zeiten && r.zeiten.length > 0 ? <Zeiten zeiten={r.zeiten} /> : r.fehlt ? '—' : null),
    },
    {
      key: 'kontakte',
      label: TERMS.wellenKontakte.label,
      tip: TERMS.wellenKontakte.tip,
      align: 'right',
      sortValue: (r) => r.zahlen?.kontakte ?? null,
      render: (r) => (r.zahlen ? int(r.zahlen.kontakte) : '—'),
    },
    {
      key: 'anmeldungen',
      label: TERMS.wellenAnmeldungen.short,
      tip: TERMS.wellenAnmeldungen.tip,
      hinweis: TERMS.wellenUeberHeatUp.tip,
      einheit: ueber,
      align: 'right',
      // A2: die Sortierung folgt dem Hauptwert der Zelle (Quote; ohne Quote die Anzahl, z. B. Redlink).
      sortValue: (r) => (r.zahlen ? (quote(r, 'anmeldungen_je_1000') ?? null) : null),
      render: (r) => (r.zahlen ? <QuoteZelle je={quote(r, 'anmeldungen_je_1000')} n={r.zahlen.anmeldungen} stellen={stJe.anmeldungen} was="Anm." /> : '—'),
    },
    {
      key: 'teilnehmer',
      label: TERMS.wellenTeilnehmer.short,
      tip: TERMS.wellenTeilnehmer.tip,
      hinweis: TERMS.wellenUeberHeatUp.tip,
      einheit: ueber,
      align: 'right',
      // Messlücke: Teilnehmer null — steht beim Sortieren in beiden Richtungen am Ende.
      sortValue: (r) => (r.zahlen && r.zahlen.teilnehmer !== null ? (quote(r, 'teilnehmer_je_1000') ?? null) : null),
      render: (r) =>
        r.zahlen ? (
          <QuoteZelle
            n={r.zahlen.teilnehmer}
            je={r.zahlen.teilnehmer === null ? undefined : quote(r, 'teilnehmer_je_1000')}
            fehltGrund={teilnahmeGrund(r.webinar)}
            stellen={stJe.teilnehmer}
            was="Teiln."
          />
        ) : (
          '—'
        ),
    },
    {
      key: 'anfos',
      label: TERMS.wellenAnfos.short,
      tip: TERMS.wellenAnfos.tip,
      hinweis: `Je Welle eines Webinars nur die Anzahl — wenige Anfos, eine Quote hinge am Zufall. Die Quoten stehen im Vergleich oben. ${TERMS.wellenUeberHeatUp.tip}`,
      einheit: TERMS.wellenUeberHeatUp.short,
      align: 'right',
      sortValue: (r) => r.zahlen?.anfos ?? null,
      render: (r) =>
        r.zahlen ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {int(r.zahlen.anfos)}
            {r.zahlen.anfos_vorlaeufig > 0 && <Unterzeile>davon {int(r.zahlen.anfos_vorlaeufig)} vorläufig</Unterzeile>}
            {/* „13 von 42 Anfos des Webinars": alle Anfos wie in der Anfo-Kachel der Detailseite. */}
            {r.art === 'webinar' && typeof r.webinar.anfos_webinar === 'number' && (
              <Unterzeile>von {int(r.webinar.anfos_webinar)} Anfos des Webinars</Unterzeile>
            )}
          </Box>
        ) : (
          '—'
        ),
    },
    {
      key: 'abmeldungen',
      label: TERMS.wellenAbmeldungen.short,
      tip: TERMS.wellenAbmeldungen.tip,
      einheit: TERMS.wellenJe1000.short,
      align: 'right',
      sortValue: (r) => (r.zahlen ? (quote(r, 'abmeldungen_je_1000') ?? null) : null),
      render: (r) => (r.zahlen ? <QuoteZelle je={quote(r, 'abmeldungen_je_1000')} n={r.zahlen.abmeldungen} stellen={stJe.abmeldungen} was="Abm." /> : '—'),
    },
  ];
  const meta = spaltenMeta(baue(true));
  const wahl = useSpaltenwahl('hebelWellen', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(sichtbar.has('versand')).filter((c) => sichtbar.has(c.key));

  const luecke = data?.schnitt_luecke;
  return (
    <ChartCard
      title="Heat-Up-Wellen je Webinar"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          Jedes Webinar soll drei Wellen bekommen: am Vortag, am Webinartag früh und am Webinartag spät. Fehlt eine, steht
          „fehlt“ statt einer Null. Oben der Vergleich der drei Wellen über alle Webinare, die alle drei hatten. Anfos zählen beim
          ersten Kontakt: frühe Wellen sammeln die Interessierten zuerst ein — weniger Anfos in einer späten Welle heißt nicht, dass
          sie verzichtbar ist.
        </>
      }
      terms={[
        { label: TERMS.welle.label, tip: TERMS.welle.tip },
        { label: ATTRIBUTION.ersterKontakt.label, tip: ATTRIBUTION.ersterKontakt.tip },
        { label: TERMS.wellenKontakte.label, tip: TERMS.wellenKontakte.tip },
        { label: TERMS.wellenTeilnehmer.label, tip: TERMS.wellenTeilnehmer.tip },
      ]}
      source="Webinar-Versände mit Empfängern, Anmeldungen, Teilnahme und Erst-Kontakt-Anfos je Versand"
      freshnessKey="webinar_send_metrics"
    >
      <Box ref={wahl.messRef}>
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {befund && <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} />}
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            Wellen im Vergleich · {int(data.vergleich.webinare)} gelaufene Webinare mit allen drei Wellen, Start ab {tag(data.von)} ({data.wochen}{' '}
            Wochen)
            {data.vergleich.ohne_teilnahme > 0 && ` (Teilnehmer ohne ${int(data.vergleich.ohne_teilnahme)} mit Messlücke)`}
          </Box>
          <SortableTable
            rows={data.vergleich.zeilen}
            columns={telefon ? vergleichSpalten.filter((c) => ['welle', 'wert', 'abm_je_anm'].includes(c.key)) : vergleichSpalten}
            rowKey={(r) => r.welle}
            initialSort={{ key: 'welle', dir: 'asc' }}
            maxHeight={null}
            festeErsteSpalte
            eng
          />
          <Box sx={{ fontSize: 13, color: 'text.secondary', mt: 3, mb: 1 }}>
            Je Webinar · Start ab {tag(data.von)} ({data.wochen} Wochen) und kommende mit Versand, neueste zuerst · nur über
            Heat-Up-Mails — die Detailseite eines Webinars zählt alle Wege und zeigt deshalb mehr
          </Box>
          <SortableTable
            rows={webinarZeilen}
            columns={spalten}
            rowKey={(r) => r.id}
            initialSort={{ key: 'name', dir: 'desc' }}
            kinder={(r) => r.kinder}
            kinderImmerOffen={() => true}
            maxHeight={null}
            festeErsteSpalte
            eng
          />
          <Hinweis>
            Welle nach Versandzeit: vor dem Webinartag = Vortag; am Webinartag vor {data.schnitt_stunde} Uhr = früh, ab{' '}
            {data.schnitt_stunde} Uhr = spät.
            {luecke && luecke.frueh_bis !== null && luecke.spaet_ab !== null &&
              ` Bei diesen Webinaren gingen die Heat-Ups am Webinartag bis ${luecke.frueh_bis} Uhr oder ab ${luecke.spaet_ab} Uhr raus — der Schnitt liegt in der Lücke.`}{' '}
            Listen, die zu einer anderen Uhrzeit verschicken (z. B. DNV um 9 Uhr), zählen zur Welle ihres Zeitfensters. Euro nur im
            Vergleich oben, über alle Webinare zusammen: Anfos × mittlerer Erwarteter Wert (bei Bestellung, netto) der Anfos ihres
            Webinars — dieselbe Bewertung wie die Karte „Webinar-Einladungen“ im Kapitel Kanäle. Je Webinar und Welle kein Euro: dafür
            sind es zu wenige Anfos. {GLOSSAR.heatUp}.
          </Hinweis>
        </>
      )}
      </Box>
    </ChartCard>
  );
};

export default WellenKarte;
